import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useMentorshipMessages } from '@/hooks/useMentorshipMessages';
import { supabase } from '@/integrations/supabase/client';
import { useDateFormat } from '@/contexts/DateFormatContext';
import type { MentorshipConnection } from '@/types/mentorship';

interface MessageThreadProps {
  connection: MentorshipConnection;
  onBack: () => void;
}

interface ProfileInfo {
  display_name: string | null;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
}

export function MessageThread({ connection, onBack }: MessageThreadProps) {
  const { user } = useAuth();
  const { formatDateTime } = useDateFormat();
  const { messages, loading, sendMessage, markAsRead } = useMentorshipMessages(connection.id);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [otherProfile, setOtherProfile] = useState<ProfileInfo | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isMentor = connection.mentor_id === user?.id;
  const otherUserId = isMentor ? connection.mentee_id : connection.mentor_id;

  useEffect(() => {
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('display_name, avatar_url, first_name, last_name')
        .eq('user_id', otherUserId)
        .single();
      
      if (data) {
        setOtherProfile(data);
      }
    };

    fetchProfile();
  }, [otherUserId]);

  useEffect(() => {
    markAsRead();
  }, [messages.length, markAsRead]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const getName = () => {
    if (otherProfile?.first_name && otherProfile?.last_name) {
      return `${otherProfile.first_name} ${otherProfile.last_name}`;
    }
    return otherProfile?.display_name || 'User';
  };

  const getInitials = (userId: string) => {
    if (userId === user?.id) return 'Me';
    if (otherProfile?.first_name && otherProfile?.last_name) {
      return `${otherProfile.first_name[0]}${otherProfile.last_name[0]}`.toUpperCase();
    }
    return otherProfile?.display_name?.slice(0, 2).toUpperCase() || 'U';
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    
    setSending(true);
    const success = await sendMessage(newMessage);
    if (success) {
      setNewMessage('');
    }
    setSending(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Avatar className="w-10 h-10">
          <AvatarImage src={otherProfile?.avatar_url || undefined} alt={getName()} />
          <AvatarFallback className="bg-primary/10 text-primary text-sm">
            {getInitials(otherUserId)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-medium">{getName()}</p>
          <p className="text-xs text-muted-foreground">
            {isMentor ? 'Your mentee' : 'Your mentor'} • {connection.skill_focus || 'General'}
          </p>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <p>No messages yet.</p>
            <p className="text-sm mt-1">Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => {
              const isMe = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}
                >
                  <Avatar className="w-8 h-8 shrink-0">
                    {isMe ? (
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        Me
                      </AvatarFallback>
                    ) : (
                      <>
                        <AvatarImage src={otherProfile?.avatar_url || undefined} />
                        <AvatarFallback className="bg-muted text-xs">
                          {getInitials(msg.sender_id)}
                        </AvatarFallback>
                      </>
                    )}
                  </Avatar>
                  <div className={`max-w-[70%] ${isMe ? 'items-end' : ''}`}>
                    <div
                      className={`px-4 py-2 rounded-2xl ${
                        isMe
                          ? 'bg-primary text-primary-foreground rounded-br-sm'
                          : 'bg-muted rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 px-1">
                      {formatDateTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={sending}
          />
          <Button onClick={handleSend} disabled={!newMessage.trim() || sending}>
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
